/*******************************************************************************
 * Copyright (c) 2013, 2015 EclipseSource.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ******************************************************************************/

package uk.co.nickthecoder.kojak

/**
 * Represents a JSON array, an ordered collection of JSON values.
 *
 * Elements can be added using the [add] methods which accept instances of
 * [JsonValue], strings, primitive numbers, and boolean values. To replace an element of an
 * array, use the [set] methods.
 *
 * Elements can be accessed by their index using [get]. This class also supports
 * iterating over the elements in document order using an [iterator] or an enhanced for
 * loop:
 *
 *     for (value in myJsonArray) {
 *          ...
 *     }
 *
 * An equivalent [List] can be obtained from the [values]}.
 *
 * Note that this class is _not thread-safe_. If multiple threads access a
 * [JsonArray] instance concurrently, while at least one of these threads modifies the
 * contents of this array, access to the instance must be synchronized externally. Failure to do so
 * may lead to an inconsistent state.
 *
 */
class JsonArray private constructor(

    values: List<JsonValue>

) : JsonValue(), Iterable<JsonValue> {

    private val mutableValues: MutableList<JsonValue> = values.toMutableList()

    constructor(array: JsonArray) : this(array.mutableValues)

    constructor() : this(mutableListOf<JsonValue>())

    /*
    constructor(array: JsonArray, unmodifiable: Boolean) : this(
        if (unmodifiable) {
            ImmutableList(array.mutableValues)
        } else {
            array.mutableValues
        }
    )
    */

    /**
     * Appends the JSON representation of the specified <code>int</code> value to the end of this
     * array.
     *
     * @param value the value to add to the array
     * @return the array itself, to enable method chaining
     */
    fun add(value: Int): JsonArray {
        mutableValues.add(Json.value(value))
        return this;
    }

    /**
     * Appends the JSON representation of the specified <code>long</code> value to the end of this
     * array.
     *
     * @param value the value to add to the array
     * @return the array itself, to enable method chaining
     */
    fun add(value: Long): JsonArray {
        mutableValues.add(Json.value(value))
        return this
    }

    /**
     * Appends the JSON representation of the specified <code>float</code> value to the end of this
     * array.
     *
     * @param value the value to add to the array
     * @return the array itself, to enable method chaining
     */
    fun add(value: Float): JsonArray {
        mutableValues.add(Json.value(value))
        return this
    }

    /**
     * Appends the JSON representation of the specified <code>double</code> value to the end of this
     * array.
     *
     * @param value the value to add to the array
     * @return the array itself, to enable method chaining
     */
    fun add(value: Double): JsonArray {
        mutableValues.add(Json.value(value))
        return this
    }

    /**
     * Appends the JSON representation of the specified <code>boolean</code> value to the end of this
     * array.
     *
     * @param value the value to add to the array
     * @return the array itself, to enable method chaining
     */
    fun add(value: Boolean): JsonArray {
        mutableValues.add(Json.value(value))
        return this
    }

    /**
     * Appends the JSON representation of the specified string to the end of this array.
     *
     * @param value the string to add to the array
     * @return the array itself, to enable method chaining
     */
    fun add(value: String?): JsonArray {
        mutableValues.add(Json.value(value))
        return this
    }

    /**
     * Appends the specified JSON value to the end of this array.
     *
     * @param value the JsonValue to add to the array, must not be <code>null</code>
     * @return the array itself, to enable method chaining
     */
    fun add(value: JsonValue): JsonArray {
        mutableValues.add(value)
        return this
    }

    /**
     * Replaces the element at the specified position in this array with the JSON representation of
     * the specified <code>int</code> value.
     *
     * @param index the index of the array element to replace
     * @param value the value to be stored at the specified array position
     * @return the array itself, to enable method chaining
     * @throws IndexOutOfBoundsException if the index is out of range, i.e. `index < 0` or `index >= size`
     */
    fun set(index: Int, value: Int): JsonArray {
        mutableValues[index] = Json.value(value)
        return this
    }

    /**
     * Replaces the element at the specified position in this array with the JSON representation of
     * the specified <code>long</code> value.
     *
     * @param index the index of the array element to replace
     * @param value the value to be stored at the specified array position
     * @return the array itself, to enable method chaining
     * @throws IndexOutOfBoundsException if the index is out of range, i.e. `index < 0` or `index >= size`
     */
    fun set(index: Int, value: Long): JsonArray {
        mutableValues[index] = Json.value(value)
        return this
    }

    /**
     * Replaces the element at the specified position in this array with the JSON representation of
     * the specified <code>float</code> value.
     *
     * @param index the index of the array element to replace
     * @param value the value to be stored at the specified array position
     * @return the array itself, to enable method chaining
     * @throws IndexOutOfBoundsException if the index is out of range, i.e. `index < 0` or `index >= size`
     */
    fun set(index: Int, value: Float): JsonArray {
        mutableValues[index] = Json.value(value)
        return this
    }

    /**
     * Replaces the element at the specified position in this array with the JSON representation of
     * the specified <code>double</code> value.
     *
     * @param index the index of the array element to replace
     * @param value the value to be stored at the specified array position
     * @return the array itself, to enable method chaining
     * @throws IndexOutOfBoundsException if the index is out of range, i.e. `index < 0` or `index >= size`
     */
    fun set(index: Int, value: Double): JsonArray {
        mutableValues.set(index, Json.value(value))
        return this
    }

    /**
     * Replaces the element at the specified position in this array with the JSON representation of
     * the specified <code>boolean</code> value.
     *
     * @param index
     *          the index of the array element to replace
     * @param value
     *          the value to be stored at the specified array position
     * @return the array itself, to enable method chaining
     * @throws IndexOutOfBoundsException if the index is out of range, i.e. `index < 0` or `index >= size`
     */
    fun set(index: Int, value: Boolean): JsonArray {
        mutableValues.set(index, Json.value(value));
        return this
    }

    /**
     * Replaces the element at the specified position in this array with the JSON representation of
     * the specified string.
     *
     * @param index the index of the array element to replace
     * @param value  the string to be stored at the specified array position
     * @return the array itself, to enable method chaining
     * @throws IndexOutOfBoundsException if the index is out of range, i.e. `index < 0` or `index >= size`
     */
    fun set(index: Int, value: String?): JsonArray {
        mutableValues[index] = Json.value(value)
        return this
    }

    /**
     * Replaces the element at the specified position in this array with the specified JSON value.
     *
     * @param index
     *          the index of the array element to replace
     * @param value
     *          the value to be stored at the specified array position, must not be <code>null</code>
     * @return the array itself, to enable method chaining
     * @throws IndexOutOfBoundsException
     *           if the index is out of range, i.e. <code>index &lt; 0</code> or
     *           <code>index &gt;= size</code>
     */
    fun set(index: Int, value: JsonValue): JsonArray {
        mutableValues[index] = value
        return this
    }

    /**
     * Removes the element at the specified index from this array.
     *
     * @param index
     *          the index of the element to remove
     * @return the array itself, to enable method chaining
     * @throws IndexOutOfBoundsException
     *           if the index is out of range, i.e. <code>index &lt; 0</code> or
     *           <code>index &gt;= size</code>
     */
    fun remove(index: Int): JsonArray {
        mutableValues.removeAt(index)
        return this
    }

    /**
     * Returns the number of elements in this array.
     *
     * @return the number of elements in this array
     */
    fun size() = mutableValues.size

    /**
     * Returns <code>true</code> if this array contains no elements.
     *
     * @return <code>true</code> if this array contains no elements
     */
    fun isEmpty() = mutableValues.isEmpty()


    /**
     * Returns the value of the element at the specified position in this array.
     *
     * @param index
     *          the index of the array element to return
     * @return the value of the element at the specified position
     * @throws IndexOutOfBoundsException
     *           if the index is out of range, i.e. <code>index &lt; 0</code> or
     *           <code>index &gt;= size</code>
     */
    fun get(index: Int) = mutableValues[index]

    /**
     * Returns a list of the values in this array in document order. The returned list is backed by
     * this array and will reflect subsequent changes. It cannot be used to modify this array.
     * Attempts to modify the returned list will result in an exception.
     *
     * @return a list of the values in this array
     */
    fun values(): List<JsonValue> = ImmutableList(mutableValues)

    /**
     * Returns an iterator over the values of this array in document order. The returned iterator
     * cannot be used to modify this array.
     *
     * @return an iterator over the values of this array
     */
    override fun iterator(): Iterator<JsonValue> {
        val iterator = mutableValues.iterator()

        return object : Iterator<JsonValue> {
            override fun hasNext() = iterator.hasNext()
            override fun next() = iterator.next()
        }
    }

    override fun write(writer: JsonWriter) {
        writer.writeArrayOpen()
        val iterator = iterator()
        if (iterator.hasNext()) {
            iterator.next().write(writer)
            while (iterator.hasNext()) {
                writer.writeArraySeparator()
                iterator.next().write(writer)
            }
        }
        writer.writeArrayClose()
    }

    override fun isArray() = true

    override fun asArray() = this

    override fun hashCode(): Int = mutableValues.hashCode()

    /**
     * Indicates whether a given object is `equal to` this [JsonArray]. An object is considered equal
     * if it is also a [JsonArray] and both arrays contain the same list of values.
     *
     * If two JsonArrays are equal, they will also produce the same JSON output.
     *
     * @param other the object to be compared with this JsonArray
     *
     * @return `true` if the specified object is equal to this JsonArray, `false` otherwise
     */
    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other == null) return false
        if (other !is JsonArray) return false
        if (other::class != this::class) return false

        return mutableValues == other.mutableValues
    }

    /*
    companion object {
        fun unmodifiableArray(array: JsonArray) = JsonArray(array, true)
    }
    */
}
